// "Supporting Documents" box under each appeal level on the parcel card. Lists the documents filed against this
// parcel / value year / level and attaches more — each one filed into Appeals / <year> (utils/appealDocs), so the
// Documents panel lists the same stored copy.
import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { DOC_KEYS, useDocVocab } from '../utils/docVocab';
import { fileUrl } from '../utils/fileUrl';
import { fileAppealDoc, loadAppealDocs } from '../utils/appealDocs';
import { ORIGINAL_NOTICE } from '../utils/notices';

// `notice` / `title`: an additional notice's appeal at this level (step 6) — its own documents, titled by its type.
export default function AppealSupportingDocs({ parcel, year, level, notice = ORIGINAL_NOTICE, title, staffUser, s, c }) {
  const { vocab, error: vocabErr } = useDocVocab();
  const types = vocab ? vocab.typesIn(DOC_KEYS.appealResults) : [];
  const [docs, setDocs] = useState([]);
  const [typeKey, setTypeKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);   // { tone: 'err' | 'warn' | 'ok', text }
  const inputRef = useRef(null);

  // isCurrent: false once the year/level has changed, so a slow response never lists another year's documents.
  async function reload(isCurrent = () => true) {
    const { data, error } = await loadAppealDocs(parcel.id, year, level, notice);
    if (!isCurrent()) return;
    if (error) { setMsg({ tone: 'err', text: `Could not load supporting documents — ${error.message}` }); return; }
    setDocs(data);
  }
  useEffect(() => {
    let alive = true;
    setMsg(null); setDocs([]);
    if (parcel?.id && year) reload(() => alive);
    return () => { alive = false; };
  }, [parcel?.id, year, level, notice]);

  async function attach(file) {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      // view mode only carries the parcel, so the property (client + MPID for the storage path) is read here.
      const { data: property, error: pe } = await supabase.from('properties')
        .select('id, name, client_id, master_property_id').eq('id', parcel.property_id).maybeSingle();
      if (pe) { setMsg({ tone: 'err', text: `Could not read the property — nothing was filed. ${pe.message}` }); return; }
      const res = await fileAppealDoc({ vocab, typeKey, property, parcel, year, level, notice, file, staffName: staffUser?.name || staffUser?.email });
      if (!res.ok) { setMsg({ tone: 'err', text: res.error }); return; }
      setMsg(res.warning ? { tone: 'warn', text: res.warning } : { tone: 'ok', text: `Filed in Appeals / ${year}.` });
      await reload();
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function open(doc) {
    try { window.open(await fileUrl(doc.storage_path, 'open'), '_blank', 'noopener'); }
    catch { setMsg({ tone: 'err', text: `Could not open ${doc.file_name}.` }); }
  }

  const toneColor = { err: '#f87171', warn: '#fbbf24', ok: '#34d399' };
  return (
    <div style={{ ...s.card, marginTop: '-8px', marginBottom: '8px' }}>
      <div style={s.cardHeader}><div style={s.cardTitle}>📎 Supporting Documents{title ? ` · ${title}` : ''}</div></div>
      <div style={{ padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {docs.map(d => (
          <div key={d.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px' }}>
            <button type="button" onClick={() => open(d)} title="Open"
              style={{ background: 'none', border: 'none', padding: 0, color: '#60a5fa', cursor: 'pointer', fontSize: '12px', textAlign: 'left', fontFamily: 'inherit' }}>
              📄 {d.file_name}
            </button>
            <span style={{ color: c.muted }}>{vocab?.type(d.type_key)?.name || ''}</span>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={typeKey} onChange={e => setTypeKey(e.target.value)} disabled={busy || !vocab}
            style={{ ...s.btnGhost, cursor: 'pointer' }}>
            <option value="">Document type…</option>
            {types.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
          <input ref={inputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => attach(e.target.files[0])} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy || !typeKey}
            title={typeKey ? '' : 'Pick a document type first'}
            style={{ ...s.btnGhost, opacity: (busy || !typeKey) ? 0.5 : 1, cursor: (busy || !typeKey) ? 'not-allowed' : 'pointer' }}>
            {busy ? 'Filing…' : '📄 Attach Supporting Document'}
          </button>
          {vocabErr && <span style={{ fontSize: '12px', color: toneColor.err }}>{vocabErr.message}</span>}
          {msg && <span style={{ fontSize: '12px', color: toneColor[msg.tone] }}>{msg.text}</span>}
        </div>
      </div>
    </div>
  );
}
